import React, { useEffect, useState } from 'react';
import {
  ArrowRight, Clock, ShieldCheck, Sparkles, BookOpen, Quote, CheckCircle2, Target,
  Award, Download, MapPin, Circle,
} from 'lucide-react';
import { PILLAR_THEME } from '../components/learning/VisualMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const displayName = (raw) => {
  if (!raw || raw === 'guest') return '';
  const token = String(raw).split('@')[0].split(/[._-]/)[0].replace(/\d+$/, '');
  return token ? token.charAt(0).toUpperCase() + token.slice(1) : '';
};

const PathwayCard = ({ course, onOpen, testIdPrefix = 'course-card' }) => {
  const theme = PILLAR_THEME[course.pillar_number];
  const p = course.progress || {};
  const done = p.status === 'completed';
  return (
    <button
      onClick={() => onOpen(course)}
      data-testid={`${testIdPrefix}-${course.pillar_number}`}
      className="group relative text-left bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 w-full"
      style={{ borderTopWidth: 4, borderTopColor: theme.primary }}
    >
      {course.is_start_here && (
        <span
          data-testid={`start-here-badge-${course.pillar_number}`}
          className="absolute -top-3 left-6 inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider text-white"
          style={{ backgroundColor: theme.primary }}
        >
          <MapPin className="h-3 w-3" /> Start here
        </span>
      )}
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: theme.primary }}>
          {course.step ? `Step ${course.step} · ` : ''}Pillar {course.pillar_number}
        </span>
        <span className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
          <Clock className="h-3.5 w-3.5" /> {course.duration_minutes} min
        </span>
      </div>
      <h3 className="text-xl font-semibold tracking-tight text-slate-900 mb-2 group-hover:underline decoration-2 underline-offset-4">
        {course.title}
      </h3>
      <p className="text-sm text-slate-600 leading-relaxed mb-5 line-clamp-2">{course.subtitle}</p>

      {done ? (
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700" data-testid={`course-complete-${course.pillar_number}`}>
          <CheckCircle2 className="h-4 w-4" /> Completed · certificate issued
        </span>
      ) : p.started ? (
        <>
          <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${p.percent}%`, backgroundColor: theme.accent }} />
          </div>
          <p className="text-xs font-medium text-slate-500 mt-2">{p.percent}% · {p.chapter_label}</p>
        </>
      ) : (
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: theme.primary }}>
          Start course <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </span>
      )}
      {course.is_role_suggestion && !done && (
        <p className="text-xs text-slate-400 mt-3">Most relevant to your role</p>
      )}
    </button>
  );
};

const CertificateRow = ({ cert }) => (
  <div
    data-testid={`certificate-${cert.certificate_id}`}
    className="flex items-center justify-between gap-4 bg-white rounded-xl border border-slate-200 p-5"
  >
    <div className="flex items-center gap-4">
      <Award className="h-6 w-6 shrink-0" style={{ color: cert.type === 'scholar' ? '#B08D57' : '#0D5C46' }} />
      <div>
        <p className="text-base font-semibold text-slate-900">{cert.title}</p>
        <p className="text-xs text-slate-500 mt-0.5">
          {cert.type === 'scholar' ? 'All six pillars' : `Pillar ${cert.pillar_number}`} · {cert.certificate_id}
        </p>
      </div>
    </div>
    <a
      href={`${BACKEND_URL}/api/learning-hub/certificate/${cert.certificate_id}/pdf`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`certificate-download-${cert.certificate_id}`}
      className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0D5C46] text-white text-sm font-semibold hover:bg-[#0A4837]"
    >
      <Download className="h-4 w-4" /> Download
    </a>
  </div>
);

const LearningHub = ({ tab = 'home', onTabChange = () => {}, onOpenCourse = () => {}, onOpenOrgDashboard = () => {} }) => {
  const userId = localStorage.getItem('cness_username') || 'guest';
  const orgId = sessionStorage.getItem('cness_organization_id') || '';
  const [role, setRole] = useState(localStorage.getItem('cness_learning_role') || 'employee');
  const [data, setData] = useState(null);
  const [roles, setRoles] = useState([]);
  const [mine, setMine] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/learning-hub/meta`).then((r) => r.json()).then((d) => setRoles(d.roles || []));
  }, []);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/learning-hub/home?user_id=${encodeURIComponent(userId)}&role=${role}`)
      .then((r) => r.json())
      .then(setData);
  }, [userId, role, tab]);

  useEffect(() => {
    if (tab !== 'mine') return;
    fetch(`${BACKEND_URL}/api/learning-hub/my-learning/${encodeURIComponent(userId)}`).then((r) => r.json()).then(setMine);
  }, [tab, userId]);

  const markDone = async (actionId) => {
    setMine((prev) => prev && { ...prev, saved_actions: prev.saved_actions.filter((a) => a.action_id !== actionId) });
    await fetch(`${BACKEND_URL}/api/learning-hub/action/${actionId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'done' }),
    });
    const r = await fetch(`${BACKEND_URL}/api/learning-hub/my-learning/${encodeURIComponent(userId)}`);
    setMine(await r.json());
  };

  if (!data) {
    return <div className="min-h-[50vh] flex items-center justify-center text-slate-400">Loading your Learning Hub…</div>;
  }

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const name = displayName(userId);
  const cont = data.continue_learning;
  const lens = data.featured_leader_lens;
  const chapters = data.chapters || [];
  const completed = data.stats.courses_completed;
  const total = data.stats.courses_total;

  return (
    <div className="space-y-12" data-testid="learning-hub">
      {/* Hero */}
      <div className="rounded-3xl bg-[#0D5C46] text-white px-6 sm:px-10 py-10 sm:py-12">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-300 mb-3">CNESS Learning Hub</p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight max-w-3xl">
          {greeting}{name ? `, ${name}` : ''}.
        </h1>
        <p className="text-base md:text-lg text-emerald-50/90 mt-4 max-w-2xl leading-relaxed">
          Six short courses, one per pillar. Each takes 15–20 minutes: understand one business idea,
          see it in real life, decide, then act.
        </p>

        <div className="flex flex-wrap items-center gap-6 mt-8">
          <div className="flex items-center gap-3" data-testid="programme-progress">
            <div className="flex items-center gap-1.5">
              {(data.pathway || []).map((c) => (
                <span
                  key={c.course_id}
                  title={c.title}
                  className="h-2.5 w-8 rounded-full"
                  style={{ backgroundColor: c.progress.status === 'completed' ? '#6EE7B7' : 'rgba(255,255,255,0.25)' }}
                />
              ))}
            </div>
            <span className="text-sm font-semibold text-emerald-100">{completed} of {total} pillars complete</span>
          </div>
          {data.stats.certificates > 0 && (
            <span className="inline-flex items-center gap-2 text-sm font-medium text-emerald-100" data-testid="hero-certificate-count">
              <Award className="h-4 w-4" /> {data.stats.certificates} certificate{data.stats.certificates === 1 ? '' : 's'}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-8">
          {['home', 'mine'].map((t) => (
            <button
              key={t}
              onClick={() => onTabChange(t)}
              data-testid={`hub-tab-${t}`}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                tab === t ? 'bg-white text-[#0D5C46]' : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              {t === 'home' ? 'Learning Home' : 'My Learning'}
            </button>
          ))}
          {orgId && (
            <button
              onClick={onOpenOrgDashboard}
              data-testid="hub-tab-org"
              className="px-5 py-2.5 rounded-full text-sm font-semibold bg-white/10 text-white hover:bg-white/20"
            >
              Organisation Dashboard
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-emerald-200">Suggest a start for</span>
            <select
              value={role}
              onChange={(e) => { setRole(e.target.value); localStorage.setItem('cness_learning_role', e.target.value); }}
              data-testid="role-select"
              className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm font-medium text-white focus:outline-none"
            >
              {roles.map((r) => <option key={r.key} value={r.key} className="text-slate-900">{r.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* How each course works — the pathway, explained once */}
      <section data-testid="how-it-works">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{data.how_it_works.headline}</h2>
        <p className="text-base text-slate-500 mt-1 mb-6">{data.how_it_works.promise}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {chapters.map((ch) => (
            <div
              key={ch.key}
              data-testid={`how-it-works-chapter-${ch.number}`}
              className="bg-white rounded-2xl border border-slate-200 p-5"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="h-7 w-7 rounded-full bg-[#0D5C46] text-white text-xs font-bold flex items-center justify-center">
                  {ch.number}
                </span>
                <span className="text-xs text-slate-400">{ch.minutes} min</span>
              </div>
              <p className="text-base font-semibold text-slate-900">{ch.label}</p>
              <p className="text-sm text-slate-500 leading-relaxed mt-1">{ch.promise}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-200 p-4" data-testid="firewall-notice">
        <ShieldCheck className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
        <p className="text-sm text-emerald-900 leading-relaxed">{data.firewall_notice}</p>
      </div>

      {tab === 'home' && (
        <>
          {cont && (
            <section>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-6">Continue where you stopped</h2>
              <div className="bg-white rounded-2xl border border-slate-200 p-8 flex flex-col lg:flex-row items-start lg:items-center gap-8 shadow-sm">
                <div className="flex-1">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] mb-2" style={{ color: PILLAR_THEME[cont.pillar_number].primary }}>
                    Pillar {cont.pillar_number} · {cont.progress.chapter_label}
                  </p>
                  <h3 className="text-2xl font-semibold text-slate-900 mb-2">{cont.title}</h3>
                  <p className="text-base text-slate-600 max-w-2xl">{cont.central_story}</p>
                  <div className="h-1.5 w-full max-w-md bg-slate-100 rounded-full overflow-hidden mt-5">
                    <div className="h-full rounded-full" style={{ width: `${cont.progress.percent}%`, backgroundColor: PILLAR_THEME[cont.pillar_number].accent }} />
                  </div>
                </div>
                <button
                  onClick={() => onOpenCourse(cont)}
                  data-testid="continue-learning-btn"
                  className="shrink-0 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#0D5C46] text-white font-semibold hover:bg-[#0A4837] transition-colors"
                >
                  Resume <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </section>
          )}

          <section>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-2">
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">Your six-pillar pathway</h2>
              <span className="text-sm font-medium text-slate-500" data-testid="pathway-progress">{completed}/{total} completed</span>
            </div>
            <p className="text-base text-slate-500 mb-6">
              Every pillar is part of the programme — all six are required for the full picture. Take them in order,
              or start with the one marked for your role.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="pathway-grid">
              {(data.pathway || []).map((c) => (
                <PathwayCard key={c.course_id} course={c} onOpen={onOpenCourse} />
              ))}
            </div>
          </section>

          {data.certificates?.length > 0 && (
            <section data-testid="hub-certificates">
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-6">Your certificates</h2>
              <div className="space-y-3">
                {data.certificates.map((c) => <CertificateRow key={c.certificate_id} cert={c} />)}
              </div>
            </section>
          )}

          <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-8">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 mb-4 flex items-center gap-2">
                <Quote className="h-4 w-4" /> Featured Leader Lens
              </p>
              <h3 className="text-2xl font-semibold text-slate-900">{lens.name} — {lens.idea}</h3>
              <p className="text-base text-slate-600 leading-relaxed mt-3">{lens.context}</p>
              <div className="mt-6 border-l-4 border-emerald-600 pl-5">
                <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-1">CNESS learning question</p>
                <p className="text-base md:text-lg text-slate-900 font-medium">{lens.learning_question}</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-700 mb-4 flex items-center gap-2">
                <Sparkles className="h-4 w-4" /> Short on time
              </p>
              <p className="text-sm text-slate-500 mb-4">Read chapters 1 and 4 only — about {data.quick_learns[0]?.quick_learn_minutes} minutes.</p>
              <div className="space-y-3">
                {data.quick_learns.map((c) => (
                  <button
                    key={c.course_id}
                    onClick={() => onOpenCourse(c)}
                    data-testid={`quick-learn-${c.pillar_number}`}
                    className="w-full text-left px-4 py-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <p className="text-sm font-semibold text-slate-900">{c.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{c.quick_learn_minutes} minutes</p>
                  </button>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {tab === 'mine' && mine && (
        <div className="space-y-12" data-testid="my-learning">
          <section className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { label: 'Pillars completed', value: `${mine.stats.courses_completed}/${mine.stats.courses_total}`, icon: CheckCircle2 },
              { label: 'Certificates', value: mine.stats.certificates, icon: Award },
              { label: 'Actions saved', value: mine.stats.actions_saved, icon: Target },
              { label: 'Actions completed', value: mine.stats.actions_done, icon: CheckCircle2 },
              { label: 'Private reflections', value: mine.stats.reflections, icon: BookOpen },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-2xl border border-slate-200 p-6" data-testid={`mine-stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
                <s.icon className="h-5 w-5 text-emerald-700 mb-3" />
                <p className="text-3xl font-bold text-slate-900">{s.value}</p>
                <p className="text-sm text-slate-500 mt-1">{s.label}</p>
              </div>
            ))}
          </section>

          <section>
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-6">My certificates</h2>
            {mine.certificates.length === 0 ? (
              <p className="text-base text-slate-500" data-testid="no-certificates">
                Finish a pillar course to earn your first learning certificate.
              </p>
            ) : (
              <div className="space-y-3" data-testid="my-certificates">
                {mine.certificates.map((c) => <CertificateRow key={c.certificate_id} cert={c} />)}
              </div>
            )}
          </section>

          <section>
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-6">Saved actions</h2>
            {mine.saved_actions.length === 0 ? (
              <p className="text-base text-slate-500">No saved actions yet. Finish a course and save a “Try This Tomorrow”.</p>
            ) : (
              <div className="space-y-3">
                {mine.saved_actions.map((a) => (
                  <div key={a.action_id} data-testid={`saved-action-${a.action_id}`} className="bg-white rounded-xl border border-slate-200 p-5 flex items-center justify-between gap-4">
                    <div>
                      <p className="text-base text-slate-900">{a.action_text}</p>
                      <p className="text-xs text-slate-500 mt-1">{a.course_title}</p>
                    </div>
                    <button
                      onClick={() => markDone(a.action_id)}
                      data-testid={`action-done-${a.action_id}`}
                      className="shrink-0 px-4 py-2 rounded-lg bg-[#0D5C46] text-white text-sm font-semibold hover:bg-[#0A4837]"
                    >
                      Mark done
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-2">My progress</h2>
            <p className="text-sm text-slate-500 mb-6">Your reflections are private. Your organisation sees participation counts only.</p>
            <div className="space-y-2 mb-8">
              {mine.courses.map((c) => (
                <div key={c.course_id} data-testid={`mine-progress-${c.pillar_number}`} className="flex items-center gap-4 bg-white rounded-xl border border-slate-200 px-5 py-4">
                  {c.progress.status === 'completed'
                    ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                    : <Circle className="h-5 w-5 text-slate-300 shrink-0" />}
                  <span className="text-base text-slate-900 flex-1">Pillar {c.pillar_number} · {c.title}</span>
                  <span className="text-sm text-slate-500">
                    {c.progress.status === 'completed' ? 'Completed' : c.progress.started ? `${c.progress.percent}% · ${c.progress.chapter_label}` : 'Not started'}
                  </span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {mine.courses.map((c) => <PathwayCard key={c.course_id} course={c} onOpen={onOpenCourse} testIdPrefix="mine-course-card" />)}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default LearningHub;
